import React from 'react';
import { DuneLines, Icon, ICONS } from '@/components/brand';
import { environment } from '@/config/environment';

interface ConnectScreenProps {
  onConnect: () => void;
  isConnecting?: boolean;
}

const explorerForChain = (chainId: number, address: string) => {
  switch (chainId) {
    case 1:
      return `https://etherscan.io/address/${address}`;
    case 11155111:
      return `https://sepolia.etherscan.io/address/${address}`;
    case 8453:
      return `https://basescan.org/address/${address}`;
    case 84532:
      return `https://sepolia.basescan.org/address/${address}`;
    default:
      return `https://blockscan.com/address/${address}`;
  }
};

const formatContract = (addr: string) => `${addr.slice(0, 6)}…${addr.slice(-4)}`;

export const ConnectScreen: React.FC<ConnectScreenProps> = ({ onConnect, isConnecting }) => {
  const factory = environment.factoryAddress;
  const explorer = explorerForChain(environment.chainId, factory);

  const facts: Array<[string, React.ReactNode]> = [
    [
      'Network',
      <>
        {environment.chainName}
        <span style={{ color: 'var(--text-muted)' }}> · ID {environment.chainId}</span>
      </>,
    ],
    [
      'Vault',
      <>
        On-chain contract
        <span style={{ color: 'var(--text-muted)' }}> · explorer-verifiable</span>
      </>,
    ],
    [
      'Custody',
      <>
        Wallet-controlled
        <span style={{ color: 'var(--text-muted)' }}> · direct claim</span>
      </>,
    ],
  ];

  return (
    <div style={{ position: 'relative', overflow: 'hidden', minHeight: 'calc(100vh - 64px)' }}>
      <DuneLines style={{ top: 0, right: 0 }} />

      <div
        className="sh-main"
        style={{
          display: 'grid',
          gridTemplateColumns: '1.05fr 1fr',
          gap: 80,
          alignItems: 'center',
          minHeight: 'calc(100vh - 64px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Left: editorial copy */}
        <div style={{ maxWidth: 520 }}>
          <div className="sh-label">SAHARA · Token Distribution</div>
          <h1
            className="sh-h1"
            style={{ fontSize: 56, marginTop: 24, letterSpacing: '-0.04em', lineHeight: 1.05 }}
          >
            View and claim your <br />
            <span className="sh-serif" style={{ color: 'var(--accent)', fontSize: 60, fontWeight: 400 }}>
              SAHARA
            </span>{' '}
            allocation.
          </h1>
          <p className="sh-sub" style={{ fontSize: 15, marginTop: 24, lineHeight: 1.6 }}>
            Connect the wallet assigned to your SAHARA allocation to view your vesting schedule,
            available balance, and upcoming unlock dates. One allocation per address — claims settle
            on-chain, direct to the wallet you control.
          </p>

          <div
            style={{
              marginTop: 36,
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span className="sh-label">Vesting vault</span>
            <span className="sh-mono" style={{ fontSize: 12, color: 'var(--text)' }}>
              {formatContract(factory)}
            </span>
            <a
              href={explorer}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 12, color: 'var(--info)' }}
            >
              View contract ↗
            </a>
          </div>

          <div style={{ marginTop: 48, display: 'flex', gap: 28 }}>
            {facts.map(([k, v]) => (
              <div
                key={k}
                style={{ flex: 1, paddingTop: 12, borderTop: '1px solid var(--border)' }}
              >
                <div className="sh-label">{k}</div>
                <div
                  style={{
                    fontSize: 12,
                    color: 'var(--text)',
                    marginTop: 8,
                    lineHeight: 1.5,
                  }}
                >
                  {v}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: connect panel */}
        <div style={{ maxWidth: 400, justifySelf: 'end', width: '100%' }}>
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-md)',
              background: 'var(--surface-1)',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '22px 24px', borderBottom: '1px solid var(--border)' }}>
              <div className="sh-label">Wallet</div>
              <div style={{ fontSize: 14, marginTop: 6, color: 'var(--text-2)' }}>
                Connect to view your vesting schedule and claim unlocked tokens.
              </div>
            </div>

            <div style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <button
                type="button"
                onClick={onConnect}
                disabled={isConnecting}
                className="sh-btn sh-btn-primary sh-btn-lg sh-btn-block"
              >
                <Icon d={ICONS.wallet} size={14} stroke={1.5} />
                {isConnecting ? 'Connecting…' : 'Connect wallet'}
                <Icon d={ICONS.arrowRight} size={14} />
              </button>
              <div className="sh-cause" style={{ textAlign: 'center' }}>
                Supports MetaMask · WalletConnect · Coinbase · Ledger · Safe
              </div>
            </div>

            <div
              style={{
                padding: '14px 24px',
                borderTop: '1px solid var(--border)',
                background: 'rgba(237,238,235,0.02)',
                fontSize: 11.5,
                color: 'var(--text-muted)',
                lineHeight: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
              }}
            >
              <Icon d={ICONS.shield} size={14} stroke={1.4} />
              <span>
                Your allocation is held onchain in a{' '}
                <span className="sh-mono" style={{ color: 'var(--text)' }}>TokenLock</span>{' '}
                contract, not in a Sahara-controlled wallet. Claims are transferred directly to your
                connected wallet. Connecting does not move funds or grant token approvals.
              </span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};
